/**
 * Utilities for chat messaging and API interactions
 */

import { Message } from '../types';
import { RetrievedSource } from '../services/api';

/**
 * Creates a new message object with timestamp
 */
export const createMessage = (
  text: string,
  isBot: boolean,
  sender: string
): Omit<Message, 'id'> => ({
  text,
  sender,
  timestamp: new Date().toISOString(),
  isBot,
});

/**
 * Convert app messages to OpenAI message format
 */
export const convertToOpenAIMessages = (messages: Message[]) => {
  return messages.map(msg => ({
    role: msg.isBot ? 'assistant' : 'user',
    content: msg.text
  }));
};

/**
 * Format messages for Bedrock API
 */
export const formatBedrockMessages = (messages: Message[], sources?: RetrievedSource[]) => {
  let formattedMessages = [];
  
  // Add system prompt if it exists in the first message
  if (messages.length > 0 && messages[0].isBot && messages[0].text.startsWith('System:')) {
    formattedMessages.push({
      role: 'system',
      content: messages[0].text.replace('System:', '').trim()
    });
    messages = messages.slice(1);
  }
  
  // Add conversation history
  for (const message of messages) {
    formattedMessages.push({
      role: message.isBot ? 'assistant' : 'user',
      content: message.text
    });
  }
  
  // If sources are provided for RAG, append them to the last user message
  if (sources && sources.length > 0 && formattedMessages.length > 0) {
    // Find the last user message
    const lastUserMessageIndex = [...formattedMessages].reverse().findIndex(m => m.role === 'user');
    if (lastUserMessageIndex !== -1) {
      const actualIndex = formattedMessages.length - 1 - lastUserMessageIndex;
      
      // Format sources
      const sourcesText = sources.map((source, index) => 
        `Source ${index + 1}: ${source.content || 'No content available'}`
      ).join('\n\n');
      
      // Append sources to the user's query
      const userMessage = formattedMessages[actualIndex];
      formattedMessages[actualIndex] = {
        ...userMessage,
        content: `${userMessage.content}\n\nHere are some relevant sources to help answer this question:\n\n${sourcesText}`
      };
    }
  }
  
  return formattedMessages;
};
